<template>
  <b-card class="export-section mb-4 shadow-sm">
    <b-card-header class="export-header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="DownloadIcon"
            size="20"
            class="mr-2 text-primary"
          />
          <h6 class="mb-0 font-weight-semibold">
            Export des données des jeunes
          </h6>
        </div>
        <b-badge
          variant="light-primary"
          class="export-badge ml-1"
        >
          Excel
        </b-badge>
      </div>
    </b-card-header>

    <b-card-body class="export-body">
      <b-row class="align-items-end">
        <!-- Filtre Type de demande -->
        <b-col lg="4" md="6" sm="12" class="mb-3 mb-lg-0">
          <b-form-group
            label="Type de demande"
            label-for="demand-type-select"
            class="mb-0"
          >
            <b-form-select
              id="demand-type-select"
              v-model="selectedDemandType"
              :options="demandTypeOptions"
              class="custom-select"
            />
          </b-form-group>
        </b-col>

        <!-- Informations période -->
        <b-col lg="4" md="6" sm="12" class="mb-3 mb-lg-0">
          <div class="period-info mt-2">
            <small class="text-muted mb-1">Période sélectionnée</small>
            <div class="period-display">
              <feather-icon
                icon="CalendarIcon"
                size="14"
                class="mr-1 text-secondary"
              />
              <span class="period-text">{{ formattedPeriod }}</span>
            </div>
          </div>
        </b-col>

        <!-- Bouton d'export -->
        <b-col lg="4" md="12" sm="12" class="text-lg-right">
          <export-button
            :demand-type="selectedDemandType"
            class="export-btn-wrapper"
          />
        </b-col>
      </b-row>

      <!-- Description des données -->
      <b-row class="mt-3">
        <b-col cols="12">
          <div class="export-description">
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="mr-1"
            />
            <small class="text-muted">
              <strong>Données incluses :</strong> heure d'appel, durée, numéro unique, âge, genre, commune, région,
              département, type de demande, motif, situation d'hébergement et notoriété.
            </small>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BBadge,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import ExportButton from './ExportButton.vue'

export default {
  name: 'ExportFilters',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BBadge,
    ExportButton,
  },
  setup() {
    const selectedDemandType = ref(0)

    const demandTypeOptions = [
      { value: 0, text: 'Tous les types' },
      { value: 1, text: 'LGBTQIA+' },
      { value: 2, text: 'Autre type' },
      { value: 3, text: 'Menaces/Canulars/Insultes' },
    ]

    const period = computed(() => store.getters['chart/getPeriod'])

    const formattedPeriod = computed(() => {
      if (!period.value.start || !period.value.end) {
        return 'Aucune période sélectionnée'
      }

      const startDate = new Date(period.value.start).toLocaleDateString('fr-FR')
      const endDate = new Date(period.value.end).toLocaleDateString('fr-FR')
      return `${startDate} - ${endDate}`
    })

    return {
      selectedDemandType,
      demandTypeOptions,
      formattedPeriod,
    }
  },
}
</script>

<style lang="scss" scoped>
  @import '~@core/scss/base/bootstrap-extended/include';
  @import '~@core/scss/base/components/variables-dark';

.export-section {
  border: 1px solid $border-color;
  border-radius: 8px;
  transition: all 0.2s ease;
  background-color: $card-bg;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}

.export-header {
  background: linear-gradient(135deg, $body-bg 0%, darken($body-bg, 3%) 100%);
  border-bottom: 1px solid $border-color;
  padding: 1rem 1.25rem;
  border-radius: 8px 8px 0 0;
}

.export-badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
}

.export-body {
  padding: 1.5rem 1.25rem;
  background-color: $card-bg;
}

.custom-select {
  border-radius: 6px;
  border: 1px solid $input-border-color;
  transition: all 0.2s ease;
  background-color: $input-bg;
  color: $body-color;

  &:focus {
    border-color: $primary;
    background-color: $input-bg;
    color: $body-color;
  }
}

.period-info {
  .period-display {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background-color: $body-bg;
    border-radius: 6px;
    border: 1px solid $border-color;
    min-height: 38px;
  }

  .period-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: $body-color;
  }
}

.export-description {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  background-color: rgba($primary, 0.05);
  border-radius: 6px;
  border-left: 5px solid $primary;
  color: $body-color;
}

.dark-layout {
  .export-section {
    border-color: $theme-dark-border-color;
    background-color: $theme-dark-card-bg;

    &:hover {
      box-shadow: $theme-dark-box-shadow !important;
    }
  }

  .export-header {
    background: linear-gradient(135deg, $theme-dark-body-bg 0%, darken($theme-dark-body-bg, 3%) 100%);
    border-bottom-color: $theme-dark-border-color;
  }

  .export-body {
    background-color: $theme-dark-card-bg;
  }

  .custom-select {
    background-color: $theme-dark-input-bg;
    border-color: $theme-dark-input-border-color;
    color: $theme-dark-body-color;

    &:focus {
      border-color: $primary;
      background-color: $theme-dark-input-bg;
    }
  }

  .period-info {
    .period-display {
      background-color: $theme-dark-body-bg;
      border-color: $theme-dark-border-color;
    }

    .period-text {
      color: $theme-dark-body-color;
    }
  }

  .export-description {
    background-color: rgba($primary, 0.1);
    color: $theme-dark-body-color;
  }
}
</style>
